import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ITimesheetFormControls } from 'src/app/interfaces/timesheets';
import { EditNoteDialogComponent } from '../edit-note-dialog/edit-note-dialog.component';
import { WeekHours } from 'src/app/models/week-hours.model';
import { MatButtonModule } from '@angular/material/button';
import { NgClass, NgIf, TitleCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { addDays, format } from 'date-fns';
import { TimesheetDate } from 'src/app/models/timesheet-date-info.model';
import { Day } from 'src/app/types/dayType';
import { daysOfWeek } from 'src/app/constants/timesheetConstants';

@Component({
    selector: 'app-timesheet-item',
    templateUrl: './timesheet-item.component.html',
    styleUrls: ['./timesheet-item.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatTooltipModule,
        MatDividerModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        NgClass,
        NgIf,
        MatButtonModule,
        MatCardModule,
        TitleCasePipe,
    ],
})
export class TimesheetItemComponent implements OnInit {
    @Input() timesheetForm: FormGroup<ITimesheetFormControls>;
    @Input() hasNotes: (
        day: Day,
        timesheet: FormGroup<ITimesheetFormControls>,
    ) => boolean;
    @Input() weekHours: WeekHours;
    @Input() timesheetDates: TimesheetDate;
    status: string = '';
    @Input() readOnly: boolean = false;
    @Output() updateTotalsEvent = new EventEmitter();
    @Output() clickRemoveEvent = new EventEmitter<
        FormGroup<ITimesheetFormControls>
    >();
    @Output() unsavedHoursEvent = new EventEmitter();

    errorText = {
        invalidDecimal: 'Hours may only have decimals in quarter intervals ',
        minimumValue: 'Hours must be 0 or greater',
        maximumValue: 'Hours must be less than 24',
        notesRequired: 'Notes are required for these hours worked',
    };

    totalHours = 0;
    protected daysOfWeek = daysOfWeek;

    constructor(public dialog: MatDialog) {}

    ngOnInit(): void {
        this.timesheetForm.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged())
            .subscribe(() => {
                this.weekHours.locked = 0;
                this.weekHours.saved = 0;
                this.weekHours.submitted = 0;
                this.timesheetForm.controls.submitted.setValue(false);
                this.timesheetForm.controls.saved.setValue(false);
                this.unsavedHoursEvent.emit();
                this.calculateTotal();
            });

        this.calculateTotal();
    }

    private calculateTotal(): void {
        let newTotal = 0;

        daysOfWeek.forEach(
            (day) => (newTotal += this.timesheetForm.controls[day.name].value),
        );

        this.totalHours = newTotal;
        this.updateTotalsEvent.emit();
    }

    protected editNoteForDay(day: string): void {
        const dialogRef = this.dialog.open(EditNoteDialogComponent, {
            height: '40%',
            width: '20%',
            data: {
                day: day + 'Notes',
                formGroup: this.timesheetForm,
                readOnly:
                    this.timesheetForm.controls.locked.value || this.readOnly,
            },
        });

        dialogRef.afterClosed().subscribe(() => {
            const noteControl = this.timesheetForm.get(day + 'Notes');
            const hoursControl = this.timesheetForm.get(day);

            if (noteControl) {
                noteControl.updateValueAndValidity({ emitEvent: false });
            }
            if (hoursControl) {
                hoursControl.updateValueAndValidity({ emitEvent: false });
            }

            this.timesheetForm.updateValueAndValidity({ emitEvent: false });
        });
    }

    tooltipText() {
        return `Client: ${this.timesheetForm.controls.clientName.value}
                Project: ${this.timesheetForm.controls.projectName.value}
                Task: ${this.timesheetForm.controls.taskName.value}
                `;
    }

    getDay(number: number) {
        return format(
            addDays(this.timesheetDates.beginningOfWeek, number),
            'MM/dd',
        );
    }
}
